import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Sparkles } from "lucide-react";
import { IMAGES } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const LINES = ["Creamos", "comunidad", "y resultados"];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 md:pt-28 pb-12 px-5 sm:px-8"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img
          src={IMAGES.heroAbstract}
          alt="Formas abstractas mango y verde"
          className="absolute right-0 top-0 h-full w-full md:w-[62%] object-cover object-center ml-auto"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-cream via-cream/40 to-transparent md:bg-gradient-to-r md:from-cream md:via-cream/70 md:to-transparent" />
      </motion.div>

      <div className="max-w-[1400px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex items-center gap-2 mb-6 text-xs font-bold tracking-[0.25em] uppercase text-ink/60"
        >
          <Sparkles size={16} className="text-green" />
          Community · Web · Diseño · Marketing
        </motion.div>

        <h1 className="font-display font-black leading-[0.9] tracking-tighter text-6xl sm:text-7xl lg:text-[8.5rem]">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line === "y resultados" ? (
                  <>
                    y <span style={{ color: "#FFC800", textShadow: "3px 3px 0 rgba(17,24,28,0.9)" }}>resultados</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 flex flex-col items-start gap-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="max-w-md text-base md:text-lg text-ink/70 leading-relaxed"
          >
            Agencia digital fresca. Gestionamos tus redes, diseñamos tu web y creamos
            la estrategia de marketing que tu marca merece.
          </motion.p>

          <motion.button
            onClick={() => scrollToId("#cita")}
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream font-bold text-base px-7 py-4 self-start"
          >
            Asesoría GRATUITA
            <span className="grid place-items-center h-8 w-8 rounded-full bg-mango text-ink group-hover:rotate-45 transition-transform duration-300">
              <ArrowDownRight size={18} />
            </span>
          </motion.button>
        </div>
      </div>
    </section>
  );
};
